import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { match as MatchType, Route, useLocation } from 'react-router';
import { Switch } from 'react-router-dom';
import universal from 'react-universal-component';

import { ConfigContext } from 'Context/config';

import { titleCaseFirstLetter } from 'Utilities';
import { defaultLazyLoadOptions } from 'Utilities/constants';
import metatitle from 'Utilities/metatitle';
import { fetchLandingPage } from 'Utilities/wordpressPages';

import DialogLoader from 'Components/transitions/DialogLoader/DialogLoader';
import Status404 from 'Containers/app/error/404';

import { createFAQData } from '../../common/FAQSection/FAQSection';

import Home from './Home/Home';
import UlezFAQ from './UlezFAQ/UlezFAQ';
import { ULEZ_URL_PATH } from './UlezPage.helpers';

const Results = universal(() => import('./Results/Results'), defaultLazyLoadOptions);

interface UlezPageProps {
  match: MatchType;
}

const UlezPage: React.FC<UlezPageProps> = ({ match }) => {
  const {
    configActions: { update },
    configState: { landingPageData },
  } = useContext(ConfigContext);

  const ulezData = landingPageData?.content;

  useEffect(() => () => {
    update({ landingPageData: undefined });
  }, [update]);

  const { pathname } = useLocation();

  const [acf, setAcf] = useState(ulezData);
  const [loading, setLoading] = useState(!ulezData);

  const faq = useMemo(() => createFAQData(acf), [acf]);

  useEffect(() => {
    const title = acf?.seo_title || acf?.header_title;
    const description = acf?.seo_description || acf?.header_description;

    if (title) {
      metatitle.formatTitle(pathname, titleCaseFirstLetter(title));
    }

    if (description) {
      metatitle.formatDescription(titleCaseFirstLetter(description));
    }
  }, [acf, pathname]);

  useEffect(() => {
    const { url } = match;

    if (!acf) {
      fetchLandingPage(
        url.replace(/\/results\/?$/, ''),
      ).then((data) => {
        setAcf(data.content);
        setLoading(false);
      }).catch((err) => {
        setLoading(false);
        window?.Sentry?.captureException(new Error(err), {
          level: 'warning',
          tags: {
            fetch: `ulez page ${url}`,
          },
        });
      });
    }
  }, [match, acf]);

  if (loading) {
    return (<DialogLoader />);
  }

  if (!acf) {
    return (<Status404 />);
  }

  return (
    <>
      <Switch>
        <Route
          exact
          path={`${ULEZ_URL_PATH}/results`}
          render={() => (<Results {...{ faq }} />)}
        />
        <Route
          render={() => (<Home {...{ acf, faq }} />)}
        />
      </Switch>
      <Route path={`${ULEZ_URL_PATH}/*`}>
        { /* The main ULEZ page implements UlezFAQ itself */}
        <UlezFAQ title={acf.faq_title} {...{ faq }} />
      </Route>
    </>
  );
};

export default UlezPage;
